/**
 * View component for /admin/authoring/scenes/:sceneId/update
 *
 * Updates a single scene from a copy of the selcted scene
 * as defined in the scene reducer
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import _ from 'lodash';

// import actions
import * as courseActions from '../../course/courseActions';
import * as nodeActions from '../../node/nodeActions';
import * as sceneActions from '../sceneActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AlertModal from "../../../global/components/modals/AlertModal.js.jsx";
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";

// import module components
import SceneForm from '../components/SceneForm.js.jsx';

// import scene css modules
import sceneStyles from '../sceneModuleStyles.css';

class AdminUpdateScene extends Base {
  constructor(props) {
    super(props);
    const { selectedScene, sceneMap } = this.props;
    this.state = {
      alertMessage: ''
      , formHelpers: {
        hasMediaType: null
        , isNewChartContent: true
        , isNewDescriptionContent: true
        , isNewTranscriptContent: true
      }
      , isAlertModalOpen: false
      , scene: sceneMap[selectedScene.id] ? _.cloneDeep(sceneMap[selectedScene.id]): {}
      // NOTE: we don't want to change the store, just make changes to a copy
    }
    this._bind(
      '_addSceneTrack'
      , '_deleteTrack'
      , '_handleFormChange'
      , '_handleFormSubmit'
      , '_handleTrackFormChange'
      , '_toggleAlertModal'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(sceneActions.fetchSingleIfNeeded(match.params.sceneId)).then( sceneRes => {
      if(sceneRes.success) {
        const currentScene = sceneRes.item
        let newFormHelpers = _.cloneDeep(this.state.formHelpers)
        newFormHelpers.hasMediaType = currentScene.pdfPath ? 'pdf' : currentScene.videoPath ? 'video' : '';
        this.setState({
          formHelpers: newFormHelpers
        })
      }
    })
    dispatch(nodeActions.fetchListIfNeeded('_scene', match.params.sceneId)).then(nodeAction => {
      let contentModulesIds = nodeAction.list && nodeAction.list.length > 0 ? nodeAction.list.map(node => node._contentModule) : null;
      dispatch(contentModuleActions.fetchListIfNeeded('_id', contentModulesIds))
      dispatch(courseActions.fetchListIfNeeded('_contentModules', contentModulesIds))
    });
  }

  componentWillReceiveProps(nextProps) {
    const { selectedScene, sceneMap } = nextProps;
    const { formHelpers } = this.state;
    let scene = sceneMap[selectedScene.id] ? _.cloneDeep(sceneMap[selectedScene.id]) : {};
    this.setState({
      alertMessage: ''
      , formHelpers: {
        hasMediaType: scene.pdfPath ? 'pdf' : scene.videoPath ? 'video' : ''
        , isNewChartContent: formHelpers.isNewChartContent
        , isNewDescriptionContent: formHelpers.isNewDescriptionContent
        , isNewTranscriptContent: formHelpers.isNewTranscriptContent
      }
      , isAlertModalOpen: false
      , scene
      //we don't want to actually change the store's scene, just use a copy
    })
  }

  _handleFormChange(e) {
    let newState = _.update( this.state, e.target.name, function() {
      return e.target.value;
    });
    // RichText content
    if(e.target.name === "scene.chart") {
      newState.formHelpers.isNewChartContent = false;
    }
    if(e.target.name === "scene.description") {
      newState.formHelpers.isNewDescriptionContent = false;
    }
    if(e.target.name === "scene.transcript") {
      newState.formHelpers.isNewTranscriptContent = false;
    }
    // Scenes should have a video OR a pdf. Never both.
    if(e.target.name === "scene.videoPath" && e.target.value) {
      newState.scene.pdfPath = ''
    }
    if(e.target.name === "scene.pdfPath" && e.target.value) {
      newState.scene.videoPath = ''
    }
    this.setState(newState);
  }

  _handleFormSubmit(e) {
    const { dispatch, history } = this.props;
    const { formHelpers } = this.state;
    e.preventDefault();
    let scene = _.cloneDeep(this.state.scene);
    if(!formHelpers.hasMediaType) {
      scene.pdfPath = ''
      scene.videoPath = ''
    }
    dispatch(sceneActions.sendUpdateScene(scene)).then((action) => {
      if(action.success) {
        history.push(`/admin/authoring/scenes/${action.item._id}`);
      } else {
        // console.log("Response Error:");
        // console.log(action);
        // alert("ERROR: "+action.error);
        this._toggleAlertModal(action.error);
      }
    });
  }

  _addSceneTrack() {
    let currentTrackList = this.state.scene.tracks;
    currentTrackList.push({ file: '', label: '' });
    this._handleFormChange({
      target: {
        name: "tracks"
        , value: currentTrackList
      }
    });
  }

  _deleteTrack(index) {
    let nextTrackList = this.state.scene.tracks;
    nextTrackList.splice(index, 1);
    this._handleFormChange({
      target: {
        name: "tracks"
        , value: nextTrackList
      }
    });
  }

  _handleTrackFormChange(e, index) {
    let nextTrackList = this.state.scene.tracks;
    if(e.name && e.value) {
      nextTrackList[index][e.name] = e.value;
    } else {
      nextTrackList[index][e.target.name] = e.target.value;
    }
    this._handleFormChange({
      target: {
        name: "tracks"
        , value: nextTrackList
      }
    });
  }

  _toggleAlertModal(message) {
    this.setState({
      alertMessage: message || ''
      , isAlertModalOpen: !this.state.isAlertModalOpen
    });
  }

  render() {
    const {
      courseStore
      , location
      , match
      , nodeStore
      , sceneId
      , selectedScene
      , contentModuleStore
    } = this.props;
    const { formHelpers, scene } = this.state;
    const isEmpty = (!scene || scene._id === null || scene._id === undefined);

    const nodeList = nodeStore.util.getList('_scene', sceneId ? sceneId : match.params.sceneId);
    const contentModuleIds = nodeList ? nodeList.map(node => node._contentModule) : null;
    const contentModuleList = contentModuleStore.util.getList('_id', contentModuleIds)
    const courseList = courseStore.util.getList('_contentModules', contentModuleIds) 

    let warningText = null
    if(contentModuleList && contentModuleList.length > 0 || courseList && courseList.length > 0) {
      warningText = 'WARNING: Make sure you are not editing a scene from an already published visit!'
    }
    return  (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
            </div>
            { isEmpty ?
              (selectedScene.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              <SceneForm
                addSceneTrack={this._addSceneTrack}
                cancelLink={`/admin/authoring/scenes/${scene._id}`}
                courseList={courseList}
                deleteTrack={this._deleteTrack}
                formHelpers={formHelpers}
                formTitle="Update Scene"
                formType="update"
                handleFormChange={this._handleFormChange}
                handleFormSubmit={this._handleFormSubmit}
                handleTrackFormChange={this._handleTrackFormChange}
                scene={scene}
                contentModuleList={contentModuleList}
                warningText={warningText}
                />
            }
          </section>
          <AlertModal
            alertMessage={this.state.alertMessage}
            alertTitle={"Error!"}
            closeAction={this._toggleAlertModal}
            confirmAction={this._toggleAlertModal}
            confirmText={"Try Again"}
            isOpen={this.state.isAlertModalOpen}
            type="danger"
            />
        </div>
      </AuthoringLayout>
    )
  }
}

AdminUpdateScene.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    courseStore: store.course
    , nodeStore: store.node
    , sceneStore: store.scene
    , sceneMap: store.scene.byId
    , selectedScene: store.scene.selected
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminUpdateScene)
);



// WEBPACK FOOTER //
// modules/scene/views/AdminUpdateScene.js.jsx